import { Analytics } from '@/utils/analytics.ts'

const hasOwnProperty = Object.prototype.hasOwnProperty

export function _has(obj, key) {
  return hasOwnProperty.call(obj, key)
}

export function _debounce(func, timeout = 300){
  let timer;
  return function(...args) {
    clearTimeout(timer)
    timer = setTimeout(() => { func.apply(this, args) }, timeout)
  }
}

/**
 * 基準点からの距離順でソート
 * 配列の中に、latitudeとlongitudeがあることが前提
 * 
 * コピーを返す方が安全ではあるが、これだけで10倍は遅くなる、実用的に並び替えで問題はないはず（問題があれば戻すか、コピーの方法を検討）
 *
 * @param {google.maps.LatLng} baseLatLng 基準の緯度軽度
 * @param {Array} list ソートする配列 [{latitude, longitude}, ...]
 * @returns {Array} ソート済の配列、距離を付与する
 */
export function sortByDistance(baseLatLng, list = []) {
  // console.debug('sortByDistance', baseLatLng, list.length)

  // NOTE:
  // sort の比較関数に距離計算を入れると、要素数 0/1 のときに比較関数が呼ばれず distance が更新されない
  // 先に全件 distance を付与してからソートする
  if (!baseLatLng) {
    return list
  }

  for (let i = 0; i < list.length; i++) {
    const p = list[i]
    const latLng =
      p?.position && typeof p.position.lat === 'function' && typeof p.position.lng === 'function'
        ? { lat: p.position.lat(), lng: p.position.lng() }
        : { lat: p.latitude, lng: p.longitude }

    p.distance = google.maps.geometry.spherical.computeDistanceBetween(baseLatLng, latLng)
  }

  list.sort((a, b) => a.distance - b.distance)

  return list
}

/**
 * @returns {Object} ページのクエリを辞書型で返す
 */
export function getParams() {
  const url = new URL(window.location.href)
  const paramDict = {}
  const params = url.searchParams

  for(const pair of params.entries()) {
    paramDict[pair[0]] = pair[1]
  }

  return paramDict
}

/**
 * 日本語の大文字→小文字の変換
 * @param {String} str 日本語
 * @returns 大文字→小文字に変換した文字列
 */
function convertJapaneseCharacters(str) {
  // 変換ルールの定義
  const conversionMap = {
    'あ': 'ぁ',
    'い': 'ぃ',
    'う': 'ぅ',
    'え': 'ぇ',
    'お': 'ぉ',
    'や': 'ゃ',
    'ゆ': 'ゅ',
    'よ': 'ょ',
    'つ': 'っ',
    'ア': 'ァ',
    'イ': 'ィ',
    'ウ': 'ぅ',
    'エ': 'ェ',
    'オ': 'ォ',
    'ヤ': 'ャ',
    'ユ': 'ュ',
    'ヨ': 'ョ',
    'ツ': 'ッ',
  };

  let result = str
  Object.keys(conversionMap).forEach(key => {
    result = result.replace(new RegExp(key, 'g'), conversionMap[key])
  })

  return result
}

/**
 * 正規化した文字列を返す、英数字は削除される
 * @param {String} text
 * @return {String}
 */
export function normalizeText(text) {
  if(!text) {
    return ''
  }

  text = String(text)
  text = text.normalize('NFKC')
  text = text.toLowerCase()
  text = text.replace(/ヶ/g, 'ケ')

  // カタカナをひらがなに変換
  text = text.replace(/[ァ-ヴ]/g, match => {
      return String.fromCharCode(match.charCodeAt(0) - 0x60)
  })
  text = convertJapaneseCharacters(text)
  text = text.replace(/\s/g, ' ')

  // 許可された文字以外を削除
  if (!storelocator.normalizeTextDeleteSymbols) {
    text = text.replace(/[^一-龠ぁ-んゔァ-ヴa-zA-Z0-9 ]/g, '')
  }

  return text
}

/**
 * URIをエンコードする
 * encodeURIだとエンコードされない文字を含めてエンコード
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/encodeURI#description
 * @param {string} uri エンコードするURI
 * @returns {string} エンコードされたURI
 */
export function enhancedEncodeURI(uri) {
  // 通常のエンコード
  let encodedURI = encodeURI(uri);

  // encodeURIだとエンコードされない文字をエンコード
  // ( ) → CSS background-imageでURIを使う際に含まれるとエラーになる
  // 追加でエンコードする文字があればここに追加
  encodedURI = encodedURI.replace(/[()]/g, (char) => {
    return '%' + char.charCodeAt(0).toString(16);
  });

  return encodedURI;
}

/**
 * バウンズが含まれているか
 * @param {google.maps.LatLngBounds} innerBounds 内包されるか確認する範囲
 * @param {google.maps.LatLngBounds} outerBounds 基準となる範囲
 * @returns {boolean} true: 内包される（innerBoundsがouterBoundsに）
 */
export function isBoundsContained(innerBounds, outerBounds) {
  const ne = innerBounds.getNorthEast()
  const sw = innerBounds.getSouthWest()

  return outerBounds.contains(ne) && outerBounds.contains(sw)
}

/**
 * 文字列からhtmlタグを除去する
 * @param {String} text
 * @returns {String} タグを除去した文字列
 * 例)　"駐車<b>場</b>" -> "駐車場"
 */
export function removeHtmlTags(text) {
  if (text.match(/<[^>]+>/)) {
    const el = document.createElement('div')
    el.innerHTML = text
    return el.textContent
  } else {
    return text
  }
}

/**
 * vue初期化とGoogleライブラリロード時のエラー処理
 * 画面全体を非表示にし、エンドユーザーへのメッセージ表示を行う
 * @param {Error} error
 */
export function handleInitError(error) {
  console.error(error) 

  // 画面全体の要素を空にしてエラーメッセージ作成
  // NOTE: createApp後にエラーが出た場合は壊れた状態で表示される可能性があるため一括でbodyを空にする
  document.body.innerHTML = '' 
  const errorContainer = document.createElement('div')
  const errorTitleText = storelocator.messages?.error_init_failed_title
  const errorTitle = errorTitleText ? `<h2 style="margin: 0 0 24px; font-size: 1.3em; font-weight: bold;">${errorTitleText}</h2>` : ''
  const errorMessage = storelocator.messages?.error_init_failed_message || 'Map loading failed. Please update your browser.'
  errorContainer.innerHTML = `<div style="padding: 16px;" role="alert" aria-live="assertive">${errorTitle}${errorMessage}</div>`
  document.body.appendChild(errorContainer)
  // analytics送信
  initAnalytics()
  storelocator.analytics?.client?.send('ExceptionThrowed',{
    ErrorMessage: error.message || error.error?.message || error.type,
    ErrorStack: error.stack || error.error?.stack,
  })
}

/**
 * Analyticsのインスタンス作成
 */
export function initAnalytics() {
  const pageKind = storelocator.page ? storelocator.page : 'map'
  if (storelocator.analytics?.client) {
    storelocator.analytics.client.context = storelocator.analytics.client.context || {}
    storelocator.analytics.client.context.PageKind = pageKind
    // すでに作成済の場合は何もしない
    return
  }
  const sendTo = storelocator.analytics?.google_analytics?.send_to
  if (sendTo) {
    const params = getParams()

    storelocator.analytics.client = new Analytics(
      sendTo,
      storelocator.analytics?.google_analytics?.debug || params.debug != undefined,
      storelocator.analytics?.google_analytics?.ignore || params.ignore != undefined,
      storelocator.analytics?.config
    )
    storelocator.analytics.client.context = storelocator.analytics.client.context || {}
    storelocator.analytics.client.context.PageKind = pageKind
  }
}